import React, { useState, useRef, useEffect } from 'react';

import { Box, BoxProps, Flex, Popover, PopoverTrigger, TextProps, useOutsideClick } from '@chakra-ui/react';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor, type Content, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { RequireAtLeastOne } from 'interfaces/utils';
import { isNil } from 'lodash';
import sanitizeHtml from 'sanitize-html';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { RTE2MenuBar } from './RTE2MenuBar';
import { LinkModal } from './components/LinkModal';
import './styles.css';

interface Props {
  placeholder?: string;
  initialText?: string;
  maxCharLimit?: number | null;
  onChange?: (htmlText: string, plainText: string, editor: Editor) => void;
  autoFocus?: boolean;
  content?: Content;
  disabled?: boolean;
  showMenuBar?: boolean;
  showCharacterCount?: boolean;
  onOutsideClick?: (htmlText: string, plainText: string) => void;
  editorMaxHeight?: string;
  testId?: string;
  alwaysShowMenuBar?: boolean;
  containerProps?: BoxProps;
  menuContainerProps?: BoxProps;
  characterCountProps?: TextProps;
  recreateOnContentChange?: boolean;
}

type RichTextEditor2Props = RequireAtLeastOne<Props, 'onChange' | 'onOutsideClick'>;

const RichTextEditor2 = ({
  content,
  maxCharLimit,
  onChange,
  placeholder = '',
  autoFocus = false,
  showMenuBar = true,
  showCharacterCount = false,
  onOutsideClick,
  editorMaxHeight = 'none',
  disabled = false,
  testId = '',
  alwaysShowMenuBar = false,
  containerProps = {},
  menuContainerProps = {},
  characterCountProps = {},
}: RichTextEditor2Props) => {
  const [charLimitError, setCharLimitError] = useState(false);

  const [editorHasText, setEditorHasText] = useState(Boolean(content?.length));

  const [editorIsFocused, setEditorIsFocused] = useState(false);

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const editorContainerRef = useRef();

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          // only passing extensions that we need
          // to disable and not the ones we need
          blockquote: false,
          codeBlock: false,
          heading: false,
          horizontalRule: false,
          code: false,
          dropcursor: false,
          gapcursor: false,
          bulletList: {
            keepAttributes: false,
            keepMarks: false,
          },
        }),
        Underline,
        Link.configure({
          HTMLAttributes: {
            class: 'rte-link',
          },
          autolink: false, // <- to prevent this issue REFER: https://www.reddit.com/r/reactjs/comments/1377qht/creating_a_link_using_tiptap/
        }),
        Placeholder.configure({
          placeholder,
        }),
        CharacterCount.configure({
          // have set the limit to null but handling it manually
          // using this extension just to get character count
          // implemented manually so that user can go beyond the limit
          // but the API call will only be made if under the limit
          limit: null,
          mode: 'textSize',
        }),
      ],
      autofocus: autoFocus,
      editable: !disabled,
      onUpdate({ editor }) {
        if (editor.storage.characterCount.characters() > 0) {
          setEditorHasText(true);
        } else {
          setEditorHasText(false);
        }
        if (editor.storage.characterCount.characters() > maxCharLimit) {
          setCharLimitError(true);
        } else {
          setCharLimitError(false);

          const htmlText: string = sanitizeHtml(editor.getHTML());
          const plainText = editor.getText();
          if (onChange) {
            if (plainText.trim() === '') {
              onChange('', '');
            } else {
              onChange(htmlText, plainText, editor);
            }
          }
        }
      },
      onCreate({ editor }) {
        if (editor.storage.characterCount.characters() > 0) {
          setEditorHasText(true);
        } else {
          setEditorHasText(false);
        }
        if (editor.storage.characterCount.characters() > maxCharLimit) {
          setCharLimitError(true);
        } else {
          setCharLimitError(false);
        }
      },
      onFocus() {
        setEditorIsFocused(true);
      },
      content,
      editorProps: {
        attributes: {
          class: 'rte-editor-2',
        },
      },
    },
    // REFER: https://github.com/ueberdosis/tiptap/issues/2403#issuecomment-1015738935
    []
  );

  // REFER: https://github.com/ueberdosis/tiptap/issues/2403#issuecomment-2567193437
  useEffect(() => {
    if (!editor || isNil(content) || content === editor.getHTML()) return;
    editor.commands.setContent(content || '');
  }, [content, editor]);

  const getSelectedText = () => {
    if (editor) {
      const { state } = editor;
      const { from, to } = state.selection;
      const selectedText = state.doc.textBetween(from, to);
      return selectedText;
    }
    return '';
  };

  useOutsideClick({
    ref: editorContainerRef,
    handler: () => {
      if (onChange) return;
      if (editorIsFocused) {
        setEditorIsFocused(false);
        if (!charLimitError) {
          const htmlText = sanitizeHtml(editor.getHTML());
          const plainText = editor.getText();
          if (!onOutsideClick) {
            return;
          }
          if (plainText.trim() === '') {
            onOutsideClick('', '');
          } else {
            onOutsideClick(htmlText, plainText);
          }
        }
      }
    },
  });

  const toggleLinkModal = () => {
    setIsLinkModalOpen(!isLinkModalOpen);
  };

  const insertLink = (link: string, text: string) => {
    const selectedText = getSelectedText();
    // if the selected text is not the same as the text in the link modal
    if (selectedText !== '' && text !== '' && text !== selectedText) {
      const { state } = editor;
      const { from, to } = state.selection;
      if (from === to) {
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .command(({ tr }) => {
          tr.insertText(text, from, to);
          return true;
        })
        .setLink({ href: link })
        .run();
    }
    // if new text is entered in the link modal
    else if (selectedText === '' && text !== '') {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: link })
        .command(({ tr }) => {
          tr.insertText(text);
          return true;
        })
        .run();
    }
    // if no text is entered in the link modal
    else if (selectedText === '' && text === '') {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: link })
        .command(({ tr }) => {
          tr.insertText(link);
          return true;
        })
        .run();
    } else {
      // if no text is selected and no text is entered in the link modal

      editor.chain().focus().extendMarkRange('link').setLink({ href: link }).run();
    }
    setIsLinkModalOpen(false);
    editor.commands.focus();
  };

  if (!editor) {
    return null;
  }

  return (
    <Box
      onClick={() => {
        setEditorIsFocused(true);
      }}
      className={`rte-box-container${editorHasText ? ' has-text' : ''}${editorIsFocused ? ' is-focused' : ''}`}
      ref={editorContainerRef}
      position="relative"
      {...containerProps}
    >
      <Flex maxHeight={editorMaxHeight}>
        <Box overflowY="auto" flex={1}>
          <EditorContent editor={editor} data-testid={testId} />
        </Box>
      </Flex>
      <Popover
        isOpen={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false);
        }}
        placement="auto-start"
      >
        {/* @ts-expect-error - children is not in Popover type */}
        <PopoverTrigger>
          <>
            {showMenuBar && !disabled && (editorIsFocused || isLinkModalOpen || alwaysShowMenuBar) && (
              <RTE2MenuBar
                editor={editor}
                toggleLinkModal={toggleLinkModal}
                isLinkModalOpen={isLinkModalOpen}
                menuContainerProps={menuContainerProps}
              />
            )}
          </>
        </PopoverTrigger>
        <LinkModal
          isOpen={isLinkModalOpen}
          selectedText={getSelectedText()}
          onClose={() => {
            setIsLinkModalOpen(false);
          }}
          onSave={insertLink}
          editorContainerRef={editorContainerRef}
        />
      </Popover>
      {showCharacterCount && (
        <TextBodyMedium ml="auto" color={charLimitError ? 'functional.C62C02' : 'text.g3'} {...characterCountProps}>
          {editor.storage.characterCount.characters()}/{maxCharLimit}
        </TextBodyMedium>
      )}
    </Box>
  );
};

export { RichTextEditor2 };
